import { ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'

import Divider from 'shared/components/Divider'
import PanelBox from 'shared/components/Panel/PanelBox'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { LinkCTA } from 'shared/components/Links'
import { PanelNames } from 'checkout/containers/CheckoutStateReducer'
import { SVGIcon } from 'shared/components/Icons'
import { colors, fonts, media } from 'shared/lib'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'

const TP = 'checkout.components.CheckoutPanel'

interface ICheckoutPanel {
  editable?: boolean
  expandedContent?: JSX.Element | null
  hideIndicator?: boolean
  name: PanelNames
  qaAttr?: string
  summarizedContent?: JSX.Element | null
  title: string | ReactNode
}

const CheckoutPanel = ({
  editable = true,
  expandedContent,
  hideIndicator,
  name,
  qaAttr,
  summarizedContent,
  title,
}: ICheckoutPanel) => {
  const { displayCheckoutRTK, editPanel, order, panels } = useCheckoutContext()
  const errorEl = useRef<HTMLDivElement>(null)

  if (!panels[name]) {
    throw new Error(
      'All CheckoutPanels must have a name that matches a panel state object in CheckoutStateReducer',
    )
  }
  const { complete, disabled, error, isEditing, view } = panels[name]

  useEffect(() => {
    const errorElTop = errorEl.current?.getBoundingClientRect().top || 0
    const scrollY = errorElTop + window.pageYOffset - 75 // offset by 75 for global nav
    window.scrollTo({ top: scrollY, behavior: 'smooth' })
  }, [error])

  useEffect(() => {
    if (isEditing && !disabled) {
      switch (name) {
        case 'guestCheckoutAuth':
          sendTrackingEvent('CHECKOUT_VIEW', {
            exposure_type: 'new',
            fastest_to_you: order?.isInstantShip,
            request_type: displayCheckoutRTK ? 'rtk' : 'gql',
            step: 'account',
          })
          break
        case 'shippingAddress':
          sendTrackingEvent('CHECKOUT_VIEW', {
            exposure_type: 'new',
            fastest_to_you: order?.isInstantShip,
            request_type: displayCheckoutRTK ? 'rtk' : 'gql',
            step: 'shipping_address',
          })
          break
        case 'shippingMethod':
          sendTrackingEvent('CHECKOUT_VIEW', {
            exposure_type: 'new',
            fastest_to_you: order?.isInstantShip,
            request_type: displayCheckoutRTK ? 'rtk' : 'gql',
            step: 'shipping_method',
          })
          break
        case 'paymentMethod':
          sendTrackingEvent('CHECKOUT_VIEW', {
            exposure_type: 'new',
            fastest_to_you: order?.isInstantShip,
            request_type: displayCheckoutRTK ? 'rtk' : 'gql',
            step: 'payment',
          })
          break
        case 'promotion':
          sendTrackingEvent('CHECKOUT_VIEW', {
            exposure_type: 'new',
            fastest_to_you: order?.isInstantShip,
            request_type: displayCheckoutRTK ? 'rtk' : 'gql',
            step: 'promo',
          })
          break
      }
    }
  }, [isEditing, disabled, name])

  return (
    <>
      {error && (
        <ErrorMessage ref={errorEl}>
          <ErrorIcon />
          <span data-qa={`CheckoutError${qaAttr}TopForm`}>{error}</span>
        </ErrorMessage>
      )}
      <Wrapper
        data-qa={`${qaAttr}Panel`}
        data-qa-panel-disabled={disabled}
        data-qa-panel-complete={complete}
        data-qa-panel-editing={isEditing}
      >
        <Header data-qa={`${qaAttr}SectionHeader`}>
          <Title>{title}</Title>
          {!hideIndicator && (
            <>
              {!disabled && editable && (isEditing === false || view === 'verification') && (
                <EditButton qaAttr={`${qaAttr}EditLink`} onClick={() => editPanel(name)}>
                  {t(`${TP}.edit`, 'Edit')}
                </EditButton>
              )}
              {complete ? <CircleCheck name="circleCheck" /> : <Circle />}
            </>
          )}
        </Header>
        {!disabled && (
          <>
            <Divider />
            {isEditing && <div data-qa={`${qaAttr}OpenedContent`}>{expandedContent}</div>}
            {!isEditing && <div data-qa={`${qaAttr}ClosedContent`}>{summarizedContent}</div>}
          </>
        )}
      </Wrapper>
    </>
  )
}

const Circle = styled.div`
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  border-radius: 50%;
  height: 18px;
  width: 18px;
`

const CircleCheck = styled(SVGIcon)`
  display: flex;
`

const EditButton = styled(LinkCTA)`
  margin-right: 15px;
  color: ${colors.FC2_GREY};
  white-space: nowrap;
  &:hover {
    color: ${colors.FC2_BLACK};
  }
`

const ErrorIcon = styled(SVGIcon).attrs({ name: 'errorIcon' })`
  margin-right: 10px;
`

const ErrorMessage = styled.div`
  ${fonts.BODY_TEXT}
  ${media.large`margin-bottom: 20px;`}
  align-items: center;
  background-color: ${colors.FC2_WHITE};
  border: 1px solid ${colors.FC2_RED};
  color: ${colors.FC2_RED};
  display: flex;
  margin-top: 20px;
  padding: 16px 20px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  ${fonts.SUBTITLE_2}
  width: 100%;
`

const Wrapper = styled(PanelBox)`
  margin-top: 15px;
  ${media.large`
    width: 515px;
    margin: 0 0 20px;
  `}
`

export default CheckoutPanel
